import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCoarsePointer, useTourState, useTourStoreApi } from "@heroncendre/player";
import { etatNavigation } from "./etatNavigation.js";
import { useEtatInfos } from "./info/etatInfos.js";
import { LookAroundHint } from "./LookAroundHint.js";
import { useReducedMotion } from "./useReducedMotion.js";

/**
 * ============================================================================
 *  IndicateurRotation.tsx — Quand montrer « regardez autour de vous »
 * ----------------------------------------------------------------------------
 *  Trois occasions, chacune une fois par page :
 *
 *   - en VISITE LIBRE, à l'arrivée sur la première scène, juste après l'accueil ;
 *   - au PREMIER CHAPITRE d'un parcours, après cinq secondes de voix : le temps
 *     que le visiteur entre dans le récit avant qu'on l'invite à bouger ;
 *   - à la FIN DE CHAQUE CHAPITRE, quand la voix s'est tue et que la caméra
 *     revient au visiteur : c'est là qu'il doit comprendre qu'il a la main.
 *
 *  Fermer l'indicateur — « J'ai compris », ou la vue tournée pour de vrai — ne
 *  vaut que pour CETTE occasion : le chapitre suivant le remontrera. Pour qu'il
 *  ne revienne plus du tout, le visiteur coche « Ne plus afficher » : le choix
 *  est alors gardé dans le navigateur (`localStorage`), d'une visite à l'autre.
 *  Si le stockage est refusé (navigation privée stricte), il vaut pour la page.
 *
 *  Un visiteur qui tourne la vue AVANT l'apparition a compris aussi :
 *  l'indicateur se ferme sans s'être montré (voir `LookAroundHint`).
 *
 *  Le menu d'informations (« Commandes de la visite ») peut le REJOUER à la
 *  demande, à tout moment : ni « déjà vu » ni « Ne plus afficher » ne s'y
 *  opposent, c'est le visiteur qui le réclame (`etatInfos.rejeu`).
 * ==========================================================================*/

const CLE = "saint-sixt.rotation-comprise";
/** Voix écoutée avant l'indicateur, au premier chapitre. */
const ATTENTE_NARRATION_S = 5;
/** Le temps de la sortie et de la pastille, avant de démonter. */
const SORTIE_MS = 3600;
/** L'occasion d'un rejeu demandé par le visiteur : jamais « déjà vue ». */
const OCCASION_REJEU = "rejeu";

function plusJamais(): boolean {
  try {
    return window.localStorage.getItem(CLE) === "1";
  } catch {
    return false;
  }
}

function retenirPlusJamais(): void {
  try {
    window.localStorage.setItem(CLE, "1");
  } catch {
    // Stockage refusé : le choix vaut pour la page (état `jamais`), rien de plus.
  }
}

type Mode = "accueil" | "libre" | "parcours" | "demo";

export function IndicateurRotation({ mode }: { mode: Mode }) {
  const [jamais, setJamais] = useState(plusJamais);
  /** Occasions déjà fermées dans cette page. */
  const [vues, setVues] = useState<ReadonlySet<string>>(() => new Set());
  /** Occasion fermée, le temps du fondu et de la pastille avant de démonter. */
  const [sortante, setSortante] = useState<{ occasion: string; jamais: boolean } | null>(null);
  const store = useTourStoreApi();
  const tactile = useCoarsePointer();
  const reduit = useReducedMotion();
  const language = useTourState((s) => s.context.language);
  const guide = mode === "parcours" || mode === "demo";

  // Un rejeu demandé par le menu d'informations : on n'y réagit qu'aux incréments POSTÉRIEURS au montage.
  const demandesRejeu = useEtatInfos((e) => e.rejeu);
  const dejaTraitees = useRef(demandesRejeu);
  const [rejeu, setRejeu] = useState(false);
  useEffect(() => {
    if (demandesRejeu === dejaTraitees.current) return;
    dejaTraitees.current = demandesRejeu;
    setSortante(null);
    setRejeu(true);
  }, [demandesRejeu]);

  // Des booléens et un rang, pas l'instant de la voix : le composant ne se
  // re-rend qu'aux franchissements, pas à chaque image.
  const momentDuChapitre = useTourState(
    (s) =>
      s.guided.stepIndex === 0 && s.guided.state === "narration" && s.narration.time >= ATTENTE_NARRATION_S,
  );
  const auPremierChapitre = useTourState(
    (s) => s.guided.stepIndex === 0 && s.guided.state !== "idle" && s.guided.state !== "chapterEnd",
  );
  const finDeChapitre = useTourState((s) => (s.guided.state === "chapterEnd" ? s.guided.stepIndex : null));
  // Pendant la voix (bandes), les plans conduisent la caméra : le rejeu ne la fait pas osciller.
  const voixConduit = useTourState((s) => s.guided.state === "narration");

  // Le moment du premier chapitre est passé une fois : l'indicateur reste, même
  // si la voix est mise en pause ou si le visiteur prend la caméra.
  const [declenche, setDeclenche] = useState(false);
  useEffect(() => {
    if (guide && momentDuChapitre) setDeclenche(true);
  }, [guide, momentDuChapitre]);

  useEffect(() => {
    if (!sortante) return;
    const minuteur = window.setTimeout(() => {
      if (sortante.occasion === OCCASION_REJEU) setRejeu(false);
      else setVues((avant) => new Set(avant).add(sortante.occasion));
      if (sortante.jamais) setJamais(true);
      setSortante(null);
    }, SORTIE_MS);
    return () => window.clearTimeout(minuteur);
  }, [sortante]);

  const occasion = rejeu
    ? OCCASION_REJEU
    : mode === "libre"
      ? "libre"
      : !guide
        ? null
        : finDeChapitre !== null
          ? `fin-${finDeChapitre}`
          : declenche && auPremierChapitre
            ? "chapitre-1"
            : null;

  const montre = rejeu || (!jamais && occasion !== null && !vues.has(occasion));

  // À l'arrivée en visite libre, l'indicateur tient l'écran : pas de titre de scène
  // par-dessus (`SceneTitle`). Un effet de MISE EN PAGE : il passe avant les effets
  // ordinaires, donc avant que le titre ne regarde si la scène est prête.
  const enLibre = montre && occasion === "libre";
  useLayoutEffect(() => {
    etatNavigation.setIndicateur(enLibre);
  }, [enLibre]);
  useLayoutEffect(() => () => etatNavigation.setIndicateur(false), []);

  // Un écran posé sur la visite ne laisse pas de points cliquables flotter sous
  // lui : ni l'accueil, ni l'indicateur. Rendus dès le « J'ai compris », sans
  // attendre la fin de la sortie : le visiteur a la main, les points arrivent
  // par leur fondu pendant que l'indicateur s'efface.
  const retenir = mode === "accueil" || (montre && sortante === null);
  useLayoutEffect(() => {
    store.getState().setHotspotsHeld(retenir);
  }, [store, retenir]);
  useLayoutEffect(() => () => store.getState().setHotspotsHeld(false), [store]);

  if (!montre || occasion === null) return null;
  // La caméra est au visiteur en visite libre et en fin de chapitre ; pendant la
  // voix, elle est aux plans, et ← → reculent et avancent la voix.
  const cameraAuVisiteur = mode === "libre" || finDeChapitre !== null || (rejeu && !voixConduit);

  return (
    <LookAroundHint
      // Une occasion, une vie : l'entrée se rejoue à chaque fin de chapitre.
      key={occasion}
      lang={language}
      device={tactile ? "touch" : "mouse"}
      reducedMotion={reduit}
      clavier={mode === "libre"}
      oscillation={cameraAuVisiteur}
      onDismiss={(nePlusAfficher) => {
        if (nePlusAfficher) retenirPlusJamais();
        setSortante({ occasion, jamais: nePlusAfficher });
      }}
    />
  );
}
